import type { Json } from './database.generated';

// ── Canonical Subject Tags (shared across community) ────────────────────────

export const SUBJECT_TAGS = [
  'Civil Procedure',
  'Constitutional Law',
  'Contracts',
  'Criminal Law',
  'Criminal Procedure',
  'Evidence',
  'Family Law',
  'Property',
  'Torts',
  'Business Associations',
  'Conflict of Laws',
  'Secured Transactions',
  'Trusts & Estates',
  'Professional Responsibility',
] as const;

export type SubjectTag = (typeof SUBJECT_TAGS)[number];

// ── Spaces ──────────────────────────────────────────────────────────────────

export interface CommunitySpace {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  sort_order: number;
  is_archived: boolean;
  created_at: string;
}

// ── User Profiles ───────────────────────────────────────────────────────────

export interface CommunityUserProfile {
  user_id: string;
  display_name: string;
  badge: string | null;
  bio: string | null;
  show_school: boolean;
  show_real_name_in_groups: boolean;
  is_banned: boolean;
  onboarded_at: string | null;
  created_at: string;
  updated_at: string;
}

// ── Posts ────────────────────────────────────────────────────────────────────

export interface CommunityPost {
  id: string;
  space_id: string;
  author_id: string;
  title: string;
  body: Json | null;
  subject_id: string | null;
  tags: string[] | null;
  vote_count: number;
  comment_count: number;
  is_pinned: boolean;
  is_locked: boolean;
  search_vector: string | null;
  edited_at: string | null;
  last_comment_at: string | null;
  deleted_at: string | null;
  deleted_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Post with joined author profile */
export interface PostWithAuthor extends CommunityPost {
  author: CommunityUserProfile;
}

/** Post with author + space info for activity feeds */
export interface PostWithContext extends PostWithAuthor {
  space: Pick<CommunitySpace, 'slug' | 'name'>;
}

// ── Comments ────────────────────────────────────────────────────────────────

export interface CommunityComment {
  id: string;
  post_id: string;
  parent_comment_id: string | null;
  author_id: string;
  body: Json | null;
  vote_count: number;
  is_ai: boolean;
  depth: number;
  search_vector: string | null;
  edited_at: string | null;
  deleted_at: string | null;
  deleted_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Comment with joined author profile */
export interface CommentWithAuthor extends CommunityComment {
  author: CommunityUserProfile;
}

/** Comment node in a rendered tree */
export interface CommentTreeNode extends CommentWithAuthor {
  children: CommentTreeNode[];
}

// ── Votes ───────────────────────────────────────────────────────────────────

export interface CommunityVote {
  id: string;
  user_id: string;
  post_id: string | null;
  comment_id: string | null;
  created_at: string;
}

// ── Bookmarks ───────────────────────────────────────────────────────────────

export interface CommunityBookmark {
  user_id: string;
  post_id: string;
  created_at: string;
}

// ── Reports ─────────────────────────────────────────────────────────────────

export type ReportReason =
  | 'spam'
  | 'harassment'
  | 'academic_integrity'
  | 'copyright'
  | 'misinformation'
  | 'crisis_concern'
  | 'other';

export type ReportStatus = 'pending' | 'reviewed' | 'actioned' | 'dismissed';

export interface CommunityReport {
  id: string;
  reporter_id: string;
  post_id: string | null;
  comment_id: string | null;
  reason: ReportReason;
  details: string | null;
  status: ReportStatus;
  reviewed_by: string | null;
  resolved_at: string | null;
  created_at: string;
}

// ── Study Groups ────────────────────────────────────────────────────────────

export type StudyGroupStatus = 'active' | 'dormant' | 'archived';
export type StudyGroupRole = 'creator' | 'member';
export type RSVPStatus = 'going' | 'maybe' | 'not_going';

export interface StudyGroup {
  id: string;
  name: string;
  subject: string | null;
  description: string | null;
  creator_id: string;
  invite_code: string | null;
  invite_created_at: string | null;
  invite_last_regenerated_at: string | null;
  max_members: number;
  status: StudyGroupStatus;
  deleted_at: string | null;
  deleted_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface StudyGroupMember {
  id: string;
  group_id: string;
  user_id: string;
  role: StudyGroupRole;
  joined_at: string;
}

/** Member with joined community profile */
export interface MemberWithProfile extends StudyGroupMember {
  profile: Pick<CommunityUserProfile, 'user_id' | 'display_name' | 'badge' | 'bio'>;
}

export interface StudyGroupMessage {
  id: string;
  group_id: string;
  author_id: string;
  body: string | null;
  reply_to_id: string | null;
  is_pinned: boolean;
  is_system: boolean;
  edited_at: string | null;
  deleted_at: string | null;
  deleted_by: string | null;
  created_at: string;
}

export interface StudyGroupAttachment {
  id: string;
  message_id: string;
  uploaded_by: string;
  storage_path: string;
  public_url: string;
  mime_type: string;
  file_name: string;
  size_bytes: number;
  width: number | null;
  height: number | null;
  scan_status: 'pending' | 'clean' | 'infected' | 'error';
  created_at: string;
  deleted_at: string | null;
}

/** Message with joined author profile */
export interface MessageWithAuthor extends StudyGroupMessage {
  author: Pick<CommunityUserProfile, 'user_id' | 'display_name' | 'badge'>;
  attachments?: StudyGroupAttachment[];
}

export interface StudyGroupSession {
  id: string;
  group_id: string;
  title: string | null;
  scheduled_at: string | null;
  duration_minutes: number | null;
  location: string | null;
  subject: string | null;
  description: string | null;
  created_by: string;
  created_at: string;
}

export interface StudyGroupReadState {
  group_id: string;
  user_id: string;
  last_read_at: string;
}

export interface StudyGroupRSVP {
  id: string;
  session_id: string;
  user_id: string;
  status: RSVPStatus;
}

export interface StudyGroupResource {
  id: string;
  group_id: string;
  added_by: string;
  type: 'file' | 'jds_link' | 'external_link' | 'pinned_message';
  title: string | null;
  url: string | null;
  tags: string[] | null;
  source_message_id: string | null;
  created_at: string;
}

/** Study group with meta from get_my_study_groups() RPC */
export interface StudyGroupWithMeta extends StudyGroup {
  member_count: number;
  last_message_at: string | null;
  last_message_body: string | null;
  last_message_is_system: boolean | null;
  last_message_author_name: string | null;
  my_role: StudyGroupRole;
  last_read_at: string | null;
}

// ── Search ────────────────────────────────────────────────────────────────

export interface CommunitySearchResult {
  id: string;
  title: string;
  body: Json | null;
  author_id: string;
  space_id: string;
  space_name: string;
  space_slug: string;
  display_name: string;
  badge: string | null;
  created_at: string;
  vote_count: number;
  comment_count: number;
  snippet: string;
  rank: number;
}

// ── Notifications ─────────────────────────────────────────────────────────

export type NotificationType =
  | 'mention'
  | 'reply_to_post'
  | 'reply_to_comment'
  | 'group_message'
  | 'group_mention'
  | 'session_scheduled'
  | 'support_reply'
  | 'support_status_change';

export type NotificationSourceType = 'post' | 'comment' | 'message' | 'group' | 'support_thread';

export interface UserNotification {
  id: string;
  user_id: string;
  type: NotificationType;
  actor_id: string | null;
  source_type: NotificationSourceType;
  source_id: string;
  title: string;
  body: string | null;
  deep_link: string | null;
  read_at: string | null;
  created_at: string;
}

// ── Mentions ──────────────────────────────────────────────────────────────

export interface CommunityMention {
  id: string;
  mentioner_id: string;
  mentioned_user_id: string;
  source_type: 'post' | 'comment' | 'message';
  source_id: string;
  created_at: string;
}

// ── Support ──────────────────────────────────────────────────────────────────

export type SupportThreadStatus = 'open' | 'being_researched' | 'working' | 'completed';
export type SupportCategory = 'account' | 'billing' | 'feature_question' | 'bug_report' | 'other';

export interface SupportThread {
  id: string;
  author_id: string;
  subject: string;
  category: SupportCategory | null;
  status: SupportThreadStatus;
  first_response_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Thread with author profile info (for admin queue) */
export interface SupportThreadWithAuthor extends SupportThread {
  author: Pick<CommunityUserProfile, 'user_id' | 'display_name' | 'badge'>;
}

export interface SupportMessage {
  id: string;
  thread_id: string;
  author_id: string;
  body: Json | null;
  reply_to_id: string | null;
  is_internal: boolean;
  edited_at: string | null;
  created_at: string;
}

/** Message with joined author profile */
export interface SupportMessageWithAuthor extends SupportMessage {
  author: Pick<CommunityUserProfile, 'user_id' | 'display_name' | 'badge'>;
}

export const SUPPORT_CATEGORIES: { value: SupportCategory; label: string }[] = [
  { value: 'account', label: 'Account' },
  { value: 'billing', label: 'Billing' },
  { value: 'feature_question', label: 'Feature Question' },
  { value: 'bug_report', label: 'Bug Report' },
  { value: 'other', label: 'Other' },
];

export const SUPPORT_STATUS_LABELS: Record<SupportThreadStatus, string> = {
  open: 'Open',
  being_researched: 'Being Researched',
  working: 'Working',
  completed: 'Completed',
};

export const SUPPORT_STATUS_COLORS: Record<SupportThreadStatus, string> = {
  open: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  being_researched: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  working: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
};

// ── Notification Preferences ─────────────────────────────────────────────────

export interface NotificationPreferences {
  user_id: string;
  email_reply_to_post: boolean;
  email_reply_to_comment: boolean;
  email_mention: boolean;
  email_group_message: boolean;
  email_group_mention: boolean;
  email_session_scheduled: boolean;
  email_support_reply: boolean;
  email_support_status_change: boolean;
  created_at: string;
  updated_at: string;
}

/** Default preferences (matches DB column defaults) */
export const DEFAULT_NOTIFICATION_PREFERENCES: Omit<NotificationPreferences, 'user_id' | 'created_at' | 'updated_at'> = {
  email_reply_to_post: true,
  email_reply_to_comment: true,
  email_mention: true,
  email_group_message: false,
  email_group_mention: true,
  email_session_scheduled: true,
  email_support_reply: true,
  email_support_status_change: true,
};

export type NotificationPrefKey = keyof typeof DEFAULT_NOTIFICATION_PREFERENCES;

export const NOTIFICATION_PREF_LABELS: Record<NotificationPrefKey, { label: string; description: string; group: string }> = {
  email_reply_to_post: { label: 'Replies to my posts', description: 'When someone replies to a discussion post you created', group: 'Discussions' },
  email_reply_to_comment: { label: 'Replies to my comments', description: 'When someone replies to your comment', group: 'Discussions' },
  email_mention: { label: '@Mentions', description: 'When someone mentions you in a post or comment', group: 'Discussions' },
  email_group_message: { label: 'Group messages', description: 'New messages in your study groups', group: 'Study Groups' },
  email_group_mention: { label: 'Group @mentions', description: 'When someone mentions you in a study group', group: 'Study Groups' },
  email_session_scheduled: { label: 'Session reminders', description: 'When a study session is scheduled in your group', group: 'Study Groups' },
  email_support_reply: { label: 'Support replies', description: 'When the support team responds to your thread', group: 'Support' },
  email_support_status_change: { label: 'Status updates', description: 'When your support thread status changes', group: 'Support' },
};

// ── Sorting ─────────────────────────────────────────────────────────────────

export type SortMode = 'new' | 'top';
export type TimePeriod = 'today' | 'week' | 'month' | 'all';

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Extract plain text from TipTap/ProseMirror JSON for display previews */
export function extractTextFromTipTap(doc: Json | null, maxLength?: number): string {
  if (!doc || typeof doc !== 'object') return '';

  const extract = (node: Json): string => {
    if (!node || typeof node !== 'object' || Array.isArray(node)) {
      if (Array.isArray(node)) return node.map(extract).join('');
      return '';
    }
    const obj = node as Record<string, Json | undefined>;
    if (obj.text && typeof obj.text === 'string') return obj.text;
    if (obj.content && Array.isArray(obj.content)) {
      return obj.content.map(extract).join(' ');
    }
    return '';
  };

  const text = extract(doc).replace(/\s+/g, ' ').trim();
  if (maxLength && text.length > maxLength) {
    return text.slice(0, maxLength) + '...';
  }
  return text;
}

/** Extract mention user IDs from TipTap/ProseMirror JSON */
export function extractMentionsFromTipTap(doc: Json | null): string[] {
  if (!doc || typeof doc !== 'object') return [];

  const ids = new Set<string>();

  const walk = (node: Json): void => {
    if (!node || typeof node !== 'object') return;
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    const obj = node as Record<string, Json | undefined>;
    if (obj.type === 'mention' && obj.attrs && typeof obj.attrs === 'object' && !Array.isArray(obj.attrs)) {
      const attrs = obj.attrs as Record<string, Json | undefined>;
      if (typeof attrs.id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(attrs.id)) {
        ids.add(attrs.id);
      }
    }
    if (Array.isArray(obj.content)) walk(obj.content);
  };

  walk(doc);
  return Array.from(ids);
}

/** Diff mentions between old and new TipTap docs (for edit notifications) */
export function diffMentions(
  oldDoc: Json | null,
  newDoc: Json | null,
): { added: string[]; removed: string[] } {
  const oldIds = new Set(extractMentionsFromTipTap(oldDoc));
  const newIds = new Set(extractMentionsFromTipTap(newDoc));

  const added = Array.from(newIds).filter((id) => !oldIds.has(id));
  const removed = Array.from(oldIds).filter((id) => !newIds.has(id));

  return { added, removed };
}

/** Format relative timestamp (e.g., "2h ago", "3d ago") */
export function formatRelativeTime(dateString: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}
